import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
 Plus,
 Search,
 Car,
 DollarSign,
 TrendingUp,
 Users,
 Calendar,
 Filter,
 X
} from 'lucide-react'
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 StatsCard,
 AlertMessage,
 Toast,
 ToastType,
 ModernInput,
 ModernSelect,
 ModernButton,
 Button,
 ListPageSkeleton,
 StatsBarSkeleton,
} from '../../components/ui'
import { ProposalsListView } from '../../components/proposals'
import { Proposal } from '../../types'
import { useProposals } from '../../hooks/useProposals'
import { DatePicker } from '../../components/form'
import { useProposalStatuses } from '../../hooks/useProposalStatuses'

export const ProposalsPage: React.FC = () => {
 const navigate = useNavigate()
 const location = useLocation()
 const { proposals, isLoading, error, pagination, fetchProposals, updateProposal, deleteProposal } = useProposals()
 const { statuses: proposalStatuses, isLoading: isLoadingStatuses } = useProposalStatuses()
 
 const [searchTerm, setSearchTerm] = useState('')
 const [statusFilter, setStatusFilter] = useState<string>('')
 const [startDate, setStartDate] = useState<string>('')
 const [endDate, setEndDate] = useState<string>('')
 const [validUntilStart, setValidUntilStart] = useState<string>('')
 const [validUntilEnd, setValidUntilEnd] = useState<string>('')
 const [customerId, setCustomerId] = useState<string>('')
 const [vehicleId, setVehicleId] = useState<string>('')
 const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
 
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
 

 // Verifica se há proposta para editar na navegação
 useEffect(() => {
 const editProposalId = (location.state as any)?.editProposalId
 if (editProposalId && proposals.length > 0) {
 const proposalToEdit = proposals.find(p => p.id === editProposalId)
 if (proposalToEdit && canEditProposal(proposalToEdit.status)) {
 // Navegar para página de edição
 navigate(`/proposals/edit/${proposalToEdit.id}`)
 // Limpa o state da navegação
 window.history.replaceState({}, document.title)
 }
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [location.state, proposals])

 // Buscar propostas com filtros
 useEffect(() => {
 const params: any = {
 page: 1,
 limit: 50
 }

 if (statusFilter) {
 params.status = statusFilter
 }

 if (searchTerm) {
 params.search = searchTerm
 }

 if (startDate) {
 params.startDate = startDate
 }

 if (endDate) {
 params.endDate = endDate
 }

 if (validUntilStart) {
 params.validUntilStart = validUntilStart
 }

 if (validUntilEnd) {
 params.validUntilEnd = validUntilEnd
 }

 if (customerId) {
 params.customerId = customerId
 }

 if (vehicleId) {
 params.vehicleId = vehicleId
 }

 const debounceTimer = setTimeout(() => {
 fetchProposals(params)
 }, searchTerm ? 500 : 0) // Debounce de 500ms para busca

 return () => clearTimeout(debounceTimer)
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchTerm, statusFilter, startDate, endDate, validUntilStart, validUntilEnd, customerId, vehicleId])

 const clearFilters = () => {
 setSearchTerm('')
 setStatusFilter('')
 setStartDate('')
 setEndDate('')
 setValidUntilStart('')
 setValidUntilEnd('')
 setCustomerId('')
 setVehicleId('')
 }

 const hasActiveFilters = () => {
 return !!(statusFilter || startDate || endDate || validUntilStart || validUntilEnd || customerId || vehicleId)
 }

 const filteredProposals = proposals

 const canEditProposal = (status: Proposal['status'] | string) => {
 const nonEditableStatuses = ['accepted', 'approved', 'rejected', 'expired']
 return !nonEditableStatuses.includes(status)
 }

 const handleOpenModal = (proposal?: Proposal) => {
 if (proposal) {
 // Verifica se pode editar antes de navegar
 if (!canEditProposal(proposal.status)) {
 setToast({ 
 message: 'Esta proposta não pode ser editada pois está com status final (aprovada, rejeitada ou expirada).', 
 type: 'error' 
 })
 return
 }
 // Navegar para página de edição
 navigate(`/proposals/edit/${proposal.id}`)
 } else {
 // Redirecionar para página de criação
 navigate('/proposals/new')
 }
 }


 const handleDelete = async (id: string) => {
 if (confirm('Tem certeza que deseja excluir esta proposta?')) {
 try {
 await deleteProposal(id)
 setToast({ message: 'Proposta excluída com sucesso!', type: 'success' })
 } catch (err) {
 console.error('Erro ao deletar proposta:', err)
 setToast({ message: 'Erro ao deletar proposta. Tente novamente.', type: 'error' })
 }
 }
 }

 const getStatusCount = (statusValue: string) => {
 return proposals.filter(proposal => proposal.status === statusValue).length
 }

 const statusOptions = [
 { value: '', label: 'Todos os Status' },
 ...proposalStatuses.map(status => ({
 value: status.value,
 label: status.label
 }))
 ]

 return (
 <div className="space-y-6">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">Propostas Abertas</h1>
 <p className="mt-1 text-sm text-muted-foreground">
 Propostas em negociação e pendentes de resposta
 </p>
 </div>
 <div className="mt-4 sm:mt-0">
 <ModernButton onClick={() => navigate('/proposals/new')}>
 <Plus className="h-4 w-4 mr-2" />
 Nova Proposta
 </ModernButton>
 </div>
 </div>

 {error && (
 <AlertMessage variant="error" message={error} />
 )}

 {/* Stats — skeleton replaces cards while loading */}
 {isLoading ? (
 <StatsBarSkeleton count={4} />
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
 <StatsCard
 title="Total de Propostas"
 value={proposals.length}
 icon={Car}
 iconColor="text-blue-600"
 />
 <StatsCard
 title="Em Negociação"
 value={getStatusCount('negotiating')}
 icon={TrendingUp}
 iconColor="text-yellow-600"
 />
 <StatsCard
 title="Aceitas"
 value={getStatusCount('accepted')}
 icon={DollarSign}
 iconColor="text-green-600"
 />
 <StatsCard
 title="Rascunhos"
 value={getStatusCount('draft')}
 icon={Users}
 iconColor="text-muted-foreground"
 />
 </div>
 )}

 {/* Filters */}
 <Card>
 <CardContent className="p-6">
 <div className="space-y-4">
 {/* Filtros básicos */}
 <div className="flex flex-col sm:flex-row gap-4">
 <div className="flex-1">
 <ModernInput
 placeholder="Pesquisar propostas..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 leftIcon={<Search className="h-4 w-4" />}
 />
 </div>
 <div className="sm:w-48">
 <ModernSelect
 value={statusFilter}
 onChange={(e) => setStatusFilter(e.target.value)}
 options={statusOptions}
 placeholder="Filtrar por status"
 disabled={isLoadingStatuses}
 />
 </div>
 <Button
 variant="outline"
 onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
 className="flex items-center gap-2"
 >
 <Filter className="h-4 w-4" />
 Filtros Avançados
 </Button>
 {hasActiveFilters() && (
 <Button
 variant="outline"
 onClick={clearFilters}
 className="flex items-center gap-2 text-red-600 hover:text-red-700"
 >
 <X className="h-4 w-4" />
 Limpar
 </Button>
 )}
 </div>

 {/* Filtros avançados */}
 {showAdvancedFilters && (
 <div className="pt-4 border-t border-border space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
 {/* Data de criação */}
 <DatePicker
 label="Data de Criação (Início)"
 value={startDate}
 onChange={(value) => setStartDate(value)}
 placeholder="Selecione a data inicial"
 />
 <DatePicker
 label="Data de Criação (Fim)"
 value={endDate}
 onChange={(value) => setEndDate(value)}
 placeholder="Selecione a data final"
 />

 {/* Data de validade */}
 <DatePicker
 label="Válido Até (Início)"
 value={validUntilStart}
 onChange={(value) => setValidUntilStart(value)}
 placeholder="Selecione a data inicial"
 />
 <DatePicker
 label="Válido Até (Fim)"
 value={validUntilEnd}
 onChange={(value) => setValidUntilEnd(value)}
 placeholder="Selecione a data final"
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {/* Cliente */}
 <div>
 <label className="block text-sm font-medium text-foreground mb-1">
 ID do Cliente
 </label>
 <ModernInput
 placeholder="Digite o ID do cliente"
 value={customerId}
 onChange={(e) => setCustomerId(e.target.value)}
 />
 </div>

 {/* Veículo */}
 <div>
 <label className="block text-sm font-medium text-foreground mb-1">
 ID do Veículo
 </label>
 <ModernInput
 placeholder="Digite o ID do veículo"
 value={vehicleId}
 onChange={(e) => setVehicleId(e.target.value)}
 />
 </div>
 </div>
 </div>
 )}
 </div>
 </CardContent>
 </Card>

 {/* Content */}
 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle>
 Lista de Propostas
 {!isLoading && ` (${pagination.total})`}
 </CardTitle>
 {!isLoading && pagination.totalPages > 1 && (
 <div className="text-sm text-muted-foreground">
 Página {pagination.page} de {pagination.totalPages}
 </div>
 )}
 </div>
 </CardHeader>
 <CardContent>
 {isLoading ? (
 <ListPageSkeleton variant="table" rows={6} showFilters={false} />
 ) : (
 <>
 <ProposalsListView
 proposals={filteredProposals}
 onViewProposal={(proposal) => navigate(`/proposals/${proposal.id}`)}
 onEditProposal={(proposal) => {
 if (canEditProposal(proposal.status)) {
 navigate(`/proposals/edit/${proposal.id}`)
 } else {
 setToast({ 
 message: 'Esta proposta não pode ser editada pois está com status final (aprovada, rejeitada ou expirada).', 
 type: 'error' 
 })
 }
 }}
 onDeleteProposal={handleDelete}
 onAddProposal={() => navigate('/proposals/new')}
 searchTerm={searchTerm}
 statusFilter={statusFilter}
 />
 
 {/* Paginação */}
 {pagination.totalPages > 1 && (
 <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
 <div className="text-sm text-muted-foreground">
 Mostrando {proposals.length} de {pagination.total} propostas
 </div>
 <div className="flex gap-2">
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => {
 const params: any = { page: pagination.page - 1, limit: 50 }
 if (statusFilter) params.status = statusFilter
 if (searchTerm) params.search = searchTerm
 if (startDate) params.startDate = startDate
 if (endDate) params.endDate = endDate
 if (validUntilStart) params.validUntilStart = validUntilStart
 if (validUntilEnd) params.validUntilEnd = validUntilEnd
 if (customerId) params.customerId = customerId
 if (vehicleId) params.vehicleId = vehicleId
 fetchProposals(params)
 }}
 disabled={pagination.page === 1}
 >
 Anterior
 </ModernButton>
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => {
 const params: any = { page: pagination.page + 1, limit: 50 }
 if (statusFilter) params.status = statusFilter
 if (searchTerm) params.search = searchTerm
 if (startDate) params.startDate = startDate
 if (endDate) params.endDate = endDate
 if (validUntilStart) params.validUntilStart = validUntilStart
 if (validUntilEnd) params.validUntilEnd = validUntilEnd
 if (customerId) params.customerId = customerId
 if (vehicleId) params.vehicleId = vehicleId
 fetchProposals(params)
 }}
 disabled={pagination.page === pagination.totalPages}
 >
 Próxima
 </ModernButton>
 </div>
 </div>
 )}
 </>
 )}
 </CardContent>
 </Card>

 {/* Toast Notifications */}
 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}
 </div>
 )
}
